import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import showToast from '../components/ToastComponent';
import { Product, SelectedFilters, SortOption } from '../interface/interface';
import { addProductToCart } from '../service/addProductToCart';
import { removeProductFromCart } from '../service/removeProductFromCart';
import { getCart } from '../service/getCart';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/userContext';
import { CartContext } from '../context/cartContext';
import AllProductsFilters from '../components/AllProductsFilters';
import {
  filterProductsBySelection,
  readSelectedCategories,
} from '../utils/productCategoryTree';
import Select, { SingleValue } from 'react-select';
import NoProductsFound from '../components/NoProductsFound';
import { WishListContext } from '../context/wishListContext';
import ProductCards from '../components/ProductCards';
import {
  customStyles,
  EMPTY_FILTERS,
  SORT_OPTIONS,
} from '../constant/constant';
import { getAccessories } from '../service/getAccessories';

function readInitialFilters(): SelectedFilters {
  if (typeof window === 'undefined') return EMPTY_FILTERS;
  const storedCategories = readSelectedCategories();
  if (!storedCategories) return EMPTY_FILTERS;
  return { ...EMPTY_FILTERS, ...storedCategories };
}

export default function NonScriptRange() {
  const {
    setLoadingEnable,
    setHideDropdown,
    setIsMobileDropdownOpen,
    setActiveItem,
  } = useContext(UserContext);
  const { setCart } = useContext(CartContext);
  const { user } = useAuth();
  const qc = useQueryClient();
  const { wishListData } = useContext(WishListContext);

  const [filters, setFilters] = useState<SelectedFilters>(readInitialFilters);
  const [sortOption, setSortOption] = useState<SortOption | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const primaryCategory = location.state?.primaryCategory;
    if (!primaryCategory) return;
    setFilters({ ...EMPTY_FILTERS, primaryCategories: [primaryCategory] });
  }, [location.key]);

  const handleFilterChange = (selected: SelectedFilters) => {
    setFilters(selected);
    if ((location.state as { primaryCategory?: string })?.primaryCategory) {
      navigate(location.pathname, { replace: true, state: null });
    }
  };

  const { isLoading } = useQuery({
    queryKey: ['cart', user?.access_token],
    queryFn: async () => {
      const result = await getCart(user!.access_token);
      if (result === undefined) {
        showToast({
          message: 'Unable to fetch existing cart!',
          type: 'error',
        });
        return null;
      }

      setCart(result);
      return result;
    },
    enabled: !!user?.access_token,
    refetchOnMount: false,
  });

  const { data: allProducts = [], isLoading: isProductsLoading } = useQuery({
    queryKey: ['nonScriptProducts'],
    queryFn: async () => {
      const result = await getAccessories(user?.access_token!, [], [], []);
      if (!result) {
        showToast({
          message: 'Unable to fetch products!',
          type: 'error',
        });
        return [];
      }
      return result as Product[];
    },
    enabled: !!user?.access_token,
  });

  useEffect(() => {
    setHideDropdown(true);
    setIsMobileDropdownOpen(false);
    setActiveItem('Shop non-script');
  }, []);

  const addToCartMutation = useMutation({
    mutationFn: async (productId: string) => {
      const result = await addProductToCart(user!.access_token, productId);
      if (result?.status === 200) {
        showToast({
          message: 'Item added to your cart.',
          type: 'success',
        });
        qc.invalidateQueries({
          queryKey: ['cart', user?.access_token],
        });
      }

      if (result?.status === 405) {
        showToast({
          message: result.data.message,
          type: 'warning',
        });
      }
      return null;
    },
    mutationKey: ['addToCart', user?.access_token],
  });

  const removeProductFromCartMutation = useMutation({
    mutationKey: ['removeCartItem', user?.access_token],
    mutationFn: async (productId: string) => {
      const result = await removeProductFromCart(user!.access_token, productId);
      if (result?.status === 200) {
        showToast({
          message: 'Item removed from your cart.',
          type: 'success',
        });
        qc.invalidateQueries({
          queryKey: ['cart', user?.access_token],
        });
      }
      if (result?.status === 405) {
        showToast({
          message: result.data.message,
          type: 'warning',
        });
      }
    },
  });

  useEffect(() => {
    if (
      !isLoading ||
      !addToCartMutation.isPending ||
      !removeProductFromCartMutation.isPending
    ) {
      setLoadingEnable(false);
    } else {
      setLoadingEnable(true);
    }
  }, [
    isLoading,
    addToCartMutation.isPending,
    removeProductFromCartMutation.isPending,
  ]);

  const filteredProducts = useMemo(
    () => filterProductsBySelection(allProducts, filters),
    [allProducts, filters],
  );

  const sortedProducts = useMemo(() => {
    if (!sortOption) return filteredProducts;

    const withParsedPrice = filteredProducts.map((p) => ({
      product: p,
      price: Number.parseFloat(p.price) || 0,
    }));

    withParsedPrice.sort((a, b) =>
      sortOption.value === 'low-to-high'
        ? a.price - b.price
        : b.price - a.price,
    );

    return withParsedPrice.map((entry) => entry.product);
  }, [filteredProducts, sortOption]);

  const handleSortChange = (option: SingleValue<SortOption>) => {
    setSortOption(option);
  };

  return (
    <div className="container-fullSpan">
      <p className="text-sm text-gray-500 block xl:hidden mb-2">
        {sortedProducts.length} product
        {sortedProducts.length !== 1 ? 's' : ''}
      </p>

      <div className="grid grid-cols-12">
        <div className="hidden xl:block col-span-3 me-5">
          <AllProductsFilters
            onFilterChange={handleFilterChange}
            allProducts={allProducts}
            selectedFilters={filters}
          />
        </div>

        <div className="xl:col-span-9 col-span-12">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex-1 xl:flex-none block xl:hidden">
                <AllProductsFilters
                  onFilterChange={handleFilterChange}
                  allProducts={allProducts}
                  selectedFilters={filters}
                />
              </div>

              <div className="xl:block hidden">
                <p className="text-sm text-gray-500">
                  {sortedProducts.length} product
                  {sortedProducts.length !== 1 ? 's' : ''}
                </p>
              </div>

              <Select
                placeholder="Sort by"
                options={SORT_OPTIONS}
                isLoading={false}
                value={sortOption}
                onChange={handleSortChange}
                isClearable
                className="flex-1 xl:flex-none rounded-md w-full md:w-44 text-xs text-primary-blue-500"
                isSearchable={false}
                styles={customStyles}
              />
            </div>

            {isProductsLoading ? (
              <p className="text-sm text-gray-400">Loading products...</p>
            ) : sortedProducts.length === 0 ? (
              <NoProductsFound
                filters={filters}
                onClearFilters={() => handleFilterChange(EMPTY_FILTERS)}
              />
            ) : (
              <ul className="grid grid-cols-2 gap-4 sm-land:grid-cols-3 md-large:grid-cols-3 xl:grid-cols-4">
                {sortedProducts.map((product) => (
                  <ProductCards
                    key={product.id}
                    isGuest={false}
                    product={product}
                    addProductToCart={async () => {
                      await addToCartMutation.mutateAsync(product.product_code);
                    }}
                    removeProductFromCart={async () => {
                      await removeProductFromCartMutation.mutateAsync(
                        product.product_code,
                      );
                    }}
                    isAccessories={false}
                    allowedProduct={false}
                    wishListProducts={wishListData}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
